import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiAgentService } from '@out-web/core';
import { finalize, Subscription } from 'rxjs';
import { IListingsResponse, IListing } from '@out-web/core';
import { ListingsService } from '@out-web/core';
import { ChatListingCardComponent, ListingCardComponent } from '@out-web/components';
import { ListingSkeletonComponent } from '@out-web/components';
import { Router } from '@angular/router';
import { marked } from 'marked';
import * as DOMPurifyModule from 'dompurify';
import { CarouselLoaderComponent } from '@out-web/components';
import { CreatedListingComponent } from './created-listing/created-listing.component';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  contentHtml?: SafeHtml; // rendered markdown HTML for assistant
  listings?: {
    url: string;
    totalCount: number;
    args: { [key: string]: string | number };
    data?: IListing[]; // store fetched listings here
    loaded?: boolean; // flag to check if already fetched
    loading?: boolean;
    Skip?: number;
  };
  createListing?: {
    canCreateListing?: boolean;
    listingId?: string | null;
    data?: IListing | null;
    loading?: boolean;
    listingCreated?: boolean;
    createListingData?: { [key: string]: string | number | boolean | null };
  };
}

interface MessagePart {
  type: 'text';
  text: {
    value: string;
  };
}

interface ApiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  parts: MessagePart[];
}

interface ApiResponse {
  response?: string;
  filters?: any;
  error?: string;
  Skip?: number;
  canCreateListing?: boolean;
  createListingData?: { [key: string]: string | number | boolean | null };
}

@Component({
  selector: 'out-ai-agent',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ListingCardComponent,
    ListingSkeletonComponent,
    CarouselLoaderComponent,
    CreatedListingComponent,
    ChatListingCardComponent
  ],
  templateUrl: './ai-agent.component.html',
  styleUrls: ['./ai-agent.component.scss'],
})
export class AiAgentComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  messages: ChatMessage[] = [];
  input = '';
  isLoading = false;
  private subscription: Subscription | null = null;
  private skipNextScroll = false;
  showListingsPanel = false;
  panelContent: string | null = null;

  activeListings: {
    messageId?: string;
    data?: IListing[];
    loading?: boolean;
    Skip?: number;
    totalCount?: number;
  } = {};

  activeListing: {
    messageId?: string;
    data?: IListing | null;
    listingId?: string | null;
    loading?: boolean;
    listingCreated?: boolean;
  } = {};

  suggestedActions = [
    {
      title: 'Find a 2023',
      label: 'Model Y under $20k',
      action: 'I am looking for a 2023 Model Y under 20000',
      icon: 'Lightbulb',
      class: 'bg-yellow-100 text-yellow-600',
    },
    {
      title: 'Show me Teslas',
      label: 'in California',
      action: 'Show me all Teslas available in California',
      icon: 'CarTaxiFront',
      class: 'bg-purple-100 text-purple-600',
    },
    {
      title: 'Do you offer',
      label: 'nationwide shipping?',
      action: 'Do you offer nationwide shipping?',
      icon: 'Truck',
      class: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'What is OnlyUsedTesla?',
      label: 'Learn how the site works',
      action: 'What is OnlyUsedTesla and how does it work?',
      icon: 'Bot',
      class: 'bg-green-100 text-green-600',
    },
  ];

  constructor(
    private aiAgentService: AiAgentService,
    private cdr: ChangeDetectorRef,
    private listingsService: ListingsService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    const nav = this.router.getCurrentNavigation();
    const stateInput = nav?.extras?.state?.['input'];

    if (stateInput) {
      this.input = stateInput;
      this.handleSubmit();
    }
  }

  ngAfterViewChecked() {
    if (!this.skipNextScroll) {
      this.scrollToBottom();
    }
    this.skipNextScroll = false;
  }

  ngOnDestroy() {
    this.handleStop();
  }

  scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  handleSubmit() {
    if (!this.input.trim() || this.isLoading) return;

    const userMessage: ChatMessage = {
      id: Math.random().toString(36).substring(7),
      role: 'user',
      content: this.input,
    };
    this.messages.push(userMessage);
    this.isLoading = true;
    const currentInput = this.input;
    this.input = '';

    const messageForApi: ApiMessage = {
      id: Math.random().toString(36).substring(7),
      role: 'user',
      content: '',
      parts: [{ type: 'text', text: { value: currentInput } }],
    };

    const messagesForApi: ApiMessage[] = this.messages.map((msg) => ({
      id: Math.random().toString(36).substring(7),
      role: msg.role,
      content: '',
      parts: [{ type: 'text', text: { value: msg.content } }],
    }));

    this.subscription = this.aiAgentService
      .sendMessage(messageForApi, messagesForApi)
      .subscribe({
        next: (data: ApiResponse) => {
          if (data.error) {
            this.messages.push({
              id: Math.random().toString(36).substring(7),
              role: 'assistant',
              content: `Sorry, there was an error: ${data.error}. Please try again.`,
            });
          } else {
            const assistantRaw = data.response || '';
            const assistantMessage: ChatMessage = {
              id: Math.random().toString(36).substring(7),
              role: 'assistant',
              content: assistantRaw,
              contentHtml: this.renderMarkdown(assistantRaw),
              listings: data.filters
                ? { ...data.filters, Skip: data.Skip }
                : undefined,
              createListing: data.canCreateListing
                ? {
                    canCreateListing: data.canCreateListing,
                    createListingData: data.createListingData,
                    listingCreated: false,
                    loading: false,
                    listingId: null,
                    data: null,
                  }
                : undefined,
            };
            this.messages.push(assistantMessage);
            // Auto-open listings panel and fetch data when AI returns listings
            if (assistantMessage.listings) {
              this.loadListings(assistantMessage);
            }
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error:', error);
          if (error.name !== 'AbortError') {
            const errorMessage = error.message || 'An unknown error occurred';
            const txt = `Sorry, there was an error: ${errorMessage}. Please try again.`;
            this.messages.push({
              id: Math.random().toString(36).substring(7),
              role: 'assistant',
              content: txt,
              contentHtml: this.renderMarkdown(txt),
            });
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isLoading = false;
          this.subscription = null;
          this.cdr.detectChanges();
        },
      });
  }

  handleStop() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
      this.isLoading = false;
    }
  }

  handleSuggestedAction(action: string) {
    this.input = action;
    this.handleSubmit();
  }

  loadListings(message: ChatMessage, isPagination = false) {
    if (!message.listings) return;

    // this.showListingsPanel = true;
    // this.panelContent = 'listings';
    this.skipNextScroll = true;

    // If already loaded, just display immediately
    if (message.listings.loaded && !isPagination) {
      this.activeListings = {
        messageId: message.id,
        data: message.listings.data,
        loading: false,
        totalCount: message.listings.totalCount,
        Skip: message.listings.Skip,
      };
      return;
    }

    // Preserve existing items during pagination so they don't disappear while loading
    const previousData: IListing[] = isPagination
      ? this.activeListings.data ?? message.listings.data ?? []
      : [];

    // Set active listings to loading (keeping previous items if paginating)
    this.activeListings = {
      messageId: message.id,
      data: previousData,
      loading: true,
      Skip: message.listings.Skip,
      totalCount: message.listings.totalCount,
    };

    const updatedFilters = { ...message.listings.args };
    this.aiAgentService
      .fetchListings(
        updatedFilters as { [key: string]: string | number },
        message.listings.Skip
      )
      .subscribe({
        next: (listingsData: IListingsResponse) => {
          // Cache inside message
          const msgIndex = this.messages.findIndex((m) => m.id === message.id);
          if (msgIndex !== -1) {
            // Ensure required listing metadata exists before updating
            if (!this.messages[msgIndex].listings) {
              return;
            }
            this.messages[msgIndex] = {
              ...this.messages[msgIndex],
              listings: {
                ...(this.messages[msgIndex].listings ?? {}),
                data: isPagination
                  ? (this.messages[msgIndex].listings?.data ?? []).concat(
                      listingsData.listings
                    )
                  : listingsData.listings,
                loaded: true,
              },
            };
          }

          // Update active display (append when paginating)
          this.activeListings = {
            messageId: message.id,
            data: isPagination
              ? previousData.concat(listingsData.listings)
              : listingsData.listings,
            loading: false,
            totalCount:
              this.messages[msgIndex !== -1 ? msgIndex : 0]?.listings
                ?.totalCount,
            Skip: this.messages[msgIndex !== -1 ? msgIndex : 0]?.listings?.Skip,
          };
        },
        error: (err) => {
          console.error('Error loading listings:', err);
          this.activeListings = {
            messageId: message.id,
            data: [],
            loading: false,
          };
        },
      });
  }

  createListing(message: ChatMessage) {
    const keyInfo = [];

    this.activeListing.loading = true;
    const msgIndex = this.messages.findIndex((m) => m.id === message.id);

    if (!this.messages[msgIndex].createListing) {
      return;
    }

    const createListing = this.messages[msgIndex].createListing;
    this.messages[msgIndex].createListing.loading = true;

    const title = `V2 / ${
      createListing?.createListingData?.['model'] || ''
    } / ${createListing?.createListingData?.['year'] || ''} / ${
      createListing?.createListingData?.['colour'] || ''
    }`;

    if (createListing?.createListingData?.['cleanHistory'])
      keyInfo.push('CleanHistory');
    if (createListing?.createListingData?.['warranty'])
      keyInfo.push('Warranty');
    if (createListing?.createListingData?.['modifications'])
      keyInfo.push('Modifications');
    if (createListing?.createListingData?.['title']) keyInfo.push('Title');
    if (createListing?.createListingData?.['fsd']) keyInfo.push('FSD');

    const FormValues = {
      type: createListing?.createListingData?.['type'] || 'ForSale',
      price: {
        currency: 'USD',
        value: createListing?.createListingData?.['price'] || 0,
      },
      location: {
        city: createListing?.createListingData?.['city'] || '',
        state: createListing?.createListingData?.['state'] || '',
        country: 'USA',
      },
      content: {
        title: title,
        description: '',
        meta: '',
        video: {
          title: null,
          description: null,
          url: '',
        },
      },
      vehicle: {
        vin: createListing?.createListingData?.['vin'] || '',
        condition: createListing?.createListingData?.['condition'] || '',
        mileage: createListing?.createListingData?.['mileage'] || 0,
        make: 'Tesla',
        model: createListing?.createListingData?.['model'] || '',
        trim: createListing?.createListingData?.['trim'] || '',
        year: createListing?.createListingData?.['year'] || '',
        lease: {
          monthsRemaining: 0,
        },
        specification: {
          colour: createListing?.createListingData?.['colour'] || '',
        },
        features: {
          autopilot: createListing?.createListingData?.['autopilotType'] || '',
        },
        keyInfo: keyInfo,
      },
    };
    this.listingsService
      .createListing(FormValues)
      .pipe(
        finalize(() => {
          this.activeListing.loading = false;
        })
      )
      .subscribe({
        next: (response: IListing) => {
          console.log('Listing created successfully:', response.id);
          if (msgIndex !== -1 && this.messages[msgIndex]?.createListing) {
            this.messages[msgIndex].createListing.listingCreated = true;
            this.messages[msgIndex].createListing.listingId = response.id;
            this.messages[msgIndex].createListing.data = response;
            this.messages[msgIndex].createListing.loading = false;
          }

          this.activeListing.listingCreated = true;
          this.activeListing.listingId = response.id;
          this.activeListing.data = response;
          this.activeListing.loading = false;
          this.activeListing.messageId = this.messages[msgIndex].id;

          this.panelContent = 'listing';
          this.showListingsPanel = true;
        },
        error: (error: any) => {
          console.error('Error creating listing:', error);

          // NEW — Send error to the AI agent so it can explain to user
          const friendlyErrorPrompt = `The vehicle listing creation failed with this error:\n\n${JSON.stringify(
            error
          )}\n\nPlease explain this to the user in plain, helpful language and suggest how to fix it.`;

          const messageForApi: ApiMessage = {
            id: Math.random().toString(36).substring(7),
            role: 'user',
            content: '',
            parts: [{ type: 'text', text: { value: friendlyErrorPrompt } }],
          };

          const messagesForApi: ApiMessage[] = this.messages.map((msg) => ({
            id: Math.random().toString(36).substring(7),
            role: msg.role,
            content: '',
            parts: [{ type: 'text', text: { value: msg.content } }],
          }));

          this.aiAgentService
            .sendMessage(messageForApi, messagesForApi)
            .subscribe((response: ApiResponse) => {
              const assistantMessage: ChatMessage = {
                id: Math.random().toString(36).substring(7),
                role: 'assistant',
                content: response.response || 'An unexpected error occurred.',
                contentHtml: this.renderMarkdown(response.response || ''),
              };
              this.messages.push(assistantMessage);
              this.activeListing.loading = false;
              if (msgIndex !== -1 && this.messages[msgIndex]?.createListing) {
                this.messages[msgIndex].createListing.listingCreated = false;
                this.messages[msgIndex].createListing.listingId = null;
                this.messages[msgIndex].createListing.data = null;
                this.messages[msgIndex].createListing.loading = false;
              }
              this.cdr.detectChanges();
            });
        },
      });
  }

  loadListing(message: ChatMessage) {
    const msgIndex = this.messages.findIndex((m) => m.id === message.id);

    if (!this.messages[msgIndex].createListing) {
      return;
    }

    this.activeListing.listingCreated = true;
    this.activeListing.loading = false;
    this.activeListing.data = this.messages[msgIndex].createListing.data;
    this.activeListing.messageId = this.messages[msgIndex].id;

    this.panelContent = 'listing';
    this.showListingsPanel = true;
  }

  // loadMoreCars() {
  //   // Ensure we have an active message to operate on
  //   if (!this.activeListings?.messageId) return;

  //   // Find the original assistant message that holds the listings/filter state
  //   const msg = this.messages.find(
  //     (m) => m.id === this.activeListings.messageId
  //   );
  //   if (!msg?.listings) return;

  //   // Safely increment Skip (it may be undefined initially)
  //   msg.listings.Skip = (msg.listings.Skip ?? 0) + 9;

  //   // Reload listings for that message
  //   this.loadListings(msg, true);
  // }

  closeListingsPanel() {
    this.showListingsPanel = false;
    this.panelContent = null;
  }

  // Lazily create a DOMPurify instance when running in the browser
  private getDOMPurify(): any | null {
    const win: any = typeof window !== 'undefined' ? window : null;
    if (!win) return null;
    const mod: any = DOMPurifyModule as any;
    const factory = mod.default ?? mod;
    // factory(window) returns a purifier instance with sanitize()
    return typeof factory === 'function' ? factory(win) : null;
  }

  // Render markdown to sanitized HTML safe for binding
  private renderMarkdown(text: string): SafeHtml {
    const html = marked.parse(text ?? '', {
      gfm: true,
      breaks: true,
    }) as string;
    const purifier = this.getDOMPurify();
    const clean = purifier
      ? purifier.sanitize(html, {
          USE_PROFILES: { html: true },
          ALLOWED_ATTR: [
            'href',
            'title',
            'target',
            'rel',
            'class',
            'src',
            'alt',
          ],
        })
      : html; // fallback without sanitization if not in browser
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }
}
